import React from 'react';
import {
    Box,
    Chip,
    IconButton,
    Link,
    Typography,
    useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    CTableWrapper,
    DeleteIcon,
    EditIcon,
    PerformanceIcon,
} from '../../../../../dum';
import {
    formatDate,
    formatFloat,
    formatRelativeDate,
} from '../../../../../../helpers';
import { OrderProductionsPageQuery } from '../../../../../../services';
import { getProductPagePath } from '../../../../../../services/navigation/production/get-route-path';
import {
    lineKey,
    PerformanceFlagChips,
    PerformanceFlagMarks,
    ProductionGrade,
} from '../../../../../smart';

type ProductionDoc =
    OrderProductionsPageQuery['paginatedOrderProductions']['docs'][number];

interface IOrderProductionCard {
    orderProduction: ProductionDoc;
    // Undefined while the batch aggregate is still resolving, and for scopes
    // that do not grade. Absence renders nothing rather than an "ok" state.
    grade?: ProductionGrade;
    onEdit: (orderProduction: ProductionDoc) => void;
    onDelete: (orderProduction: ProductionDoc) => void;
    onGrade: (orderProduction: ProductionDoc) => void;
}

/**
 * Card body for one production run inside the Producción CCardList. Header
 * (fecha/tipo/sucursal/turno), employee chips + desperdicio, and the
 * product-machine sub-table with actions.
 */
export function OrderProductionCard({
    orderProduction,
    grade,
    onEdit,
    onDelete,
    onGrade,
}: IOrderProductionCard) {
    const theme = useTheme();
    // Grading needs at least one complete product×machine line to compare
    // against the combo's history.
    const canGrade = orderProduction.order_production_products.some(
        (opp) => opp.product?.id && opp.machine?.id,
    );
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Typography variant={'subtitle2'}>
                    {formatDate(orderProduction.start_date)}
                </Typography>
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                >
                    (act. {formatRelativeDate(orderProduction.updated_at)}
                    {orderProduction.updated_by
                        ? ` por ${orderProduction.updated_by.fullname}`
                        : ''}
                    )
                </Typography>
                {orderProduction.order_production_type ? (
                    <Chip
                        size={'small'}
                        label={orderProduction.order_production_type.name}
                    />
                ) : null}
                <PerformanceFlagChips grade={grade} />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mb: 1,
                }}
            >
                {orderProduction.order_production_employees
                    .filter((ope) => ope.employee)
                    .map((ope) => (
                        <Chip
                            key={ope.employee!.fullname}
                            label={ope.employee!.fullname}
                            size={'small'}
                            variant={'outlined'}
                        />
                    ))}
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    sx={{ ml: 1 }}
                >
                    Desperdicio {formatFloat(orderProduction.waste)} kg
                </Typography>
            </Box>
            <CTableWrapper
                padding={false}
                baseColor={subBaseColor}
                stripeColor={subStripeColor}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 60,
                        content: (opp) =>
                            opp.product?.id ? (
                                <Link
                                    component={RouterLink}
                                    to={getProductPagePath({
                                        productId: opp.product.id,
                                    })}
                                    underline={'hover'}
                                    color={'inherit'}
                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                >
                                    {opp.product.compound_description}
                                </Link>
                            ) : (
                                opp.product?.compound_description
                            ),
                    },
                    {
                        title: 'Maquina',
                        cellGrow: 30,
                        // The flag belongs to the product x machine combo, so it
                        // is marked on the line that identifies that combo.
                        content: (opp) => (
                            <>
                                {opp.machine?.name}
                                {opp.product?.id && opp.machine?.id ? (
                                    <PerformanceFlagMarks
                                        line={grade?.byLine.get(
                                            lineKey(
                                                opp.product.id,
                                                opp.machine.id,
                                            ),
                                        )}
                                    />
                                ) : null}
                            </>
                        ),
                    },
                    {
                        title: 'Grupos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (opp) => formatFloat(opp.groups),
                    },
                    {
                        title: 'Kilos',
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (opp) => formatFloat(opp.kilos),
                    },
                ]}
                data={orderProduction.order_production_products}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 1,
                }}
            >
                <Typography variant={'body2'}>
                    {orderProduction.branch?.name}
                    {orderProduction.shift != null
                        ? ` · turno ${orderProduction.shift}`
                        : ''}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton
                    size={'small'}
                    disabled={!canGrade}
                    onClick={(e) => {
                        // The whole card is clickable (opens edit) — keep the
                        // buttons from double-firing through it.
                        e.stopPropagation();
                        onGrade(orderProduction);
                    }}
                >
                    <PerformanceIcon />
                </IconButton>
                <IconButton
                    size={'small'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(orderProduction);
                    }}
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    size={'small'}
                    disabled={!orderProduction.is_deletable}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(orderProduction);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
        </Box>
    );
}
